"use client"

import React from "react"
import dayjs from "dayjs"

interface ReceiptModalProps {
  item: any
  items?: any[]
  onClose: () => void
}

const ReceiptModal: React.FC<ReceiptModalProps> = ({ item, items, onClose }) => {
  const receiptItems = items && items.length > 0 ? items : [item]
  const isBulk = receiptItems.length > 1

  // Dates
  const date = isBulk
    ? `${dayjs(receiptItems[receiptItems.length - 1].createdon).format("DD/MM/YYYY")} - ${dayjs(receiptItems[0].createdon).format("DD/MM/YYYY")}`
    : dayjs(item.createdon).format("DD/MM/YYYY")

  const salesId = item.salesid || item.id || 0
  const billNo = `CS${String(salesId).padStart(8, "0")}`
  const customerName =
    item.customername || item.clientusername || "Walk-in Customer"

  // Totals
  const grandTotal = receiptItems.reduce(
    (sum, i) => sum + Number(i.totalprice || 0),
    0
  )
  const grandTotalFormatted = grandTotal.toLocaleString("en-KE")

  // Org config from localStorage
  const clientUser = localStorage.getItem("clientuser")
    ? JSON.parse(localStorage.getItem("clientuser") || "{}")
    : {}
  const orgConfig = clientUser?.clientData?.appconfig || {}
  const orgName = clientUser?.clientData?.name || "HAWKERS PRIDE"
  const branchName = orgConfig.branchName || "MAIN"
  const poBox = orgConfig.poBox || ""
  const phoneNumber = orgConfig.phoneNumber || ""
  const pinNumber = orgConfig.pinNumber || ""
  const vatNumber = orgConfig.vatNumber || ""
  const businessName = orgConfig.businessName || orgName

  // Build HTML item rows for print
  const buildItemRows = () =>
    receiptItems
      .map(
        (i) => `
        <tr>
          <td>${i.name}</td>
          <td class="right">${Number(i.quantity || 0)}</td>
          <td class="right">${Number(i.sellingprice || 0).toLocaleString("en-KE")}</td>
          <td class="right">${Number(i.totalprice || 0).toLocaleString("en-KE")}</td>
        </tr>
      `
      )
      .join("")

  const receiptStyles = `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Courier New', Courier, monospace;
      width: 300px;
      margin: 0 auto;
      padding: 14px 10px;
      font-size: 11px;
      line-height: 1.55;
    }
    .center { text-align: center; }
    .right { text-align: right; }
    .bold { font-weight: bold; }
    .store-name { font-size: 15px; font-weight: bold; text-align: center; letter-spacing: 1px; margin-bottom: 2px; }
    .store-branch { font-size: 12px; font-weight: bold; text-align: center; }
    .info-block { text-align: center; font-size: 11px; }
    hr.solid { border: none; border-top: 1px solid #000; margin: 5px 0; }
    .meta-grid { display: flex; justify-content: space-between; font-size: 11px; margin: 3px 0; }
    .customer { font-size: 12px; font-weight: bold; margin: 3px 0; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 4px; }
    th { text-align: left; font-weight: bold; padding: 2px 0; border-bottom: 2px solid #000; text-decoration: underline; }
    th.right, td.right { text-align: right; }
    td { padding: 3px 0; border-bottom: 1px dashed #ddd; }
    .grand-total-row { display: flex; justify-content: space-between; font-weight: bold; font-size: 13px; margin-top: 5px; }
    .footer { text-align: center; font-size: 10px; margin-top: 14px; }
    @media print {
      @page { size: 80mm auto; margin: 0; }
      body { width: 72mm; padding: 10px 8px; }
    }
  `

  const handlePrint = () => {
    const printWindow = window.open("", "", "height=800,width=460")
    if (!printWindow) return
    printWindow.document.write(`
      <html>
      <head>
        <title>Receipt - ${billNo}</title>
        <style>${receiptStyles}</style>
      </head>
      <body>
        <div class="store-name">${orgName}</div>
        ${branchName ? `<div class="store-branch">- ${branchName} -</div>` : ""}
        ${poBox ? `<div class="info-block">${poBox}</div>` : ""}
        ${phoneNumber ? `<div class="info-block">TEL: ${phoneNumber}</div>` : ""}
        ${pinNumber ? `<div class="info-block">PIN: ${pinNumber}</div>` : ""}
        ${vatNumber ? `<div class="info-block">VAT: ${vatNumber}</div>` : ""}
        <hr class="solid">
        <div class="center bold" style="font-size:13px;">CASH SALE</div>
        <div class="center" style="font-size:10px;">(Print Copy 1) ORIGINAL</div>
        <hr class="solid">
        <div class="meta-grid">
          <span>${date}</span>
          <span>${billNo}</span>
        </div>
        <div class="customer">${customerName}-</div>
        <hr class="solid">
        <table>
          <thead>
            <tr>
              <th>ITEM</th>
              <th class="right">QTY</th>
              <th class="right">PRICE</th>
              <th class="right">TOTAL</th>
            </tr>
          </thead>
          <tbody>
            ${buildItemRows()}
          </tbody>
        </table>
        <hr class="solid">
        <div class="grand-total-row">
          <span>GRAND TOTAL:</span>
          <span>KES ${grandTotalFormatted}</span>
        </div>
        <div class="footer">
          <div>Thank you for shopping with us!</div>
          <div class="bold">${businessName}</div>
        </div>
      </body>
      </html>
    `)
    printWindow.document.close()
    setTimeout(() => {
      printWindow.print()
      printWindow.close()
    }, 400)
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
      <div className="bg-white w-full max-w-[380px] rounded-2xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="bg-gray-900 text-white px-5 py-3 text-center">
          <div className="text-lg font-bold tracking-widest">
            {isBulk
              ? `Receipt Preview (${receiptItems.length} items)`
              : "Receipt Preview"}
          </div>
        </div>

        {/* Receipt Preview */}
        <div className="px-5 py-4 font-mono bg-white max-h-[65vh] overflow-y-auto text-[11px] leading-relaxed">

          {/* Store Info */}
          <div className="text-center font-bold text-sm tracking-wide">{orgName}</div>
          {branchName && (
            <div className="text-center font-bold text-xs">- {branchName} -</div>
          )}
          {poBox && <div className="text-center text-xs">{poBox}</div>}
          {phoneNumber && <div className="text-center text-xs">TEL: {phoneNumber}</div>}
          {pinNumber && <div className="text-center text-xs">PIN: {pinNumber}</div>}
          {vatNumber && <div className="text-center text-xs">VAT: {vatNumber}</div>}

          <div className="border-t border-gray-900 my-2" />

          {/* Sale Type */}
          <div className="text-center font-bold text-sm">CASH SALE</div>
          <div className="text-center text-[9px]">(Print Copy 1) ORIGINAL</div>

          <div className="border-t border-gray-900 my-2" />

          {/* Date + Bill No */}
          <div className="flex justify-between text-[10px]">
            <span>{date}</span>
            <span>{billNo}</span>
          </div>

          {/* Customer */}
          <div className="font-bold text-xs mt-1">{customerName}-</div>

          <div className="border-t border-gray-900 my-2" />

          {/* Column Headers */}
          <div className="grid grid-cols-[1fr,28px,56px,56px] gap-x-1 text-[10px] font-bold border-b-2 border-gray-900 pb-1">
            <div className="underline">ITEM</div>
            <div className="text-right underline">QTY</div>
            <div className="text-right underline">PRICE</div>
            <div className="text-right underline">TOTAL</div>
          </div>

          {/* Line Items */}
          {receiptItems.map((i, idx) => (
            <div
              key={idx}
              className="grid grid-cols-[1fr,28px,56px,56px] gap-x-1 text-[10px] py-1.5 border-b border-dashed border-gray-200 last:border-0"
            >
              <div className="truncate">{i.name}</div>
              <div className="text-right">{Number(i.quantity || 0)}</div>
              <div className="text-right">
                {Number(i.sellingprice || 0).toLocaleString("en-KE")}
              </div>
              <div className="text-right font-bold">
                {Number(i.totalprice || 0).toLocaleString("en-KE")}
              </div>
            </div>
          ))}

          <div className="border-t border-gray-900 my-1" />

          {/* Grand Total */}
          <div className="flex justify-between font-bold text-xs py-1">
            <span>GRAND TOTAL:</span>
            <span>KES {grandTotalFormatted}</span>
          </div>

          {/* Footer */}
          <div className="text-center text-[9px] mt-4 opacity-60">
            <div>Thank you for shopping with us!</div>
              <div className="font-bold">{businessName}</div>
          </div>
        </div>

        {/* Print Button */}
        <div className="p-3 border-t bg-gray-50">
          <button
            onClick={handlePrint}
            className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3 rounded-xl text-sm font-medium transition"
          >
            Print / Download
          </button>
        </div>

        <button
          onClick={onClose}
          className="w-full py-3 text-sm text-gray-500 hover:bg-gray-100 transition"
        >
          Close
        </button>
      </div>
    </div>
  )
}

export default ReceiptModal